import AppLayout from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StrandBadge from "@/components/StrandBadge";
import ModeBadge from "@/components/ModeBadge";
import ProgressRing from "@/components/ProgressRing";
import StartSelfServeButton from "@/components/StartSelfServeButton";
import { 
  Mic, 
  ArrowRight, 
  Clock, 
  Play, 
  CheckCircle, 
  FileText, 
  Trophy,
  Target,
  TrendingUp,
  Star,
  Award,
  Calendar,
  Plus,
  BarChart3,
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAssessments, useCurrentUser } from "@/lib/api";
import { useMemo } from "react";

export default function StudentDashboard() {
  const { data: user } = useCurrentUser();
  const { data: assessments, isLoading } = useAssessments();

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case "pending": return { label: "Not Started", color: "text-muted-foreground", bg: "bg-secondary" };
      case "uploading": return { label: "Uploading", color: "text-blue-500", bg: "bg-blue-100 dark:bg-blue-900" };
      case "processing": return { label: "Processing", color: "text-yellow-500", bg: "bg-yellow-100 dark:bg-yellow-900" };
      case "draft_ready": return { label: "Ready for Review", color: "text-green-500", bg: "bg-green-100 dark:bg-green-900" };
      case "under_review": return { label: "In Review", color: "text-purple-500", bg: "bg-purple-100 dark:bg-purple-900" };
      case "signed_off": return { label: "Completed", color: "text-green-600", bg: "bg-green-100 dark:bg-green-900" };
      case "error": return { label: "Error", color: "text-red-500", bg: "bg-red-100 dark:bg-red-900" };
      default: return { label: status, color: "text-muted-foreground", bg: "bg-secondary" };
    }
  };

  const { stats, strandProgress, pendingTasks, completedTasks, recentFeedback, nextMilestone } = useMemo(() => {
    if (!assessments || !Array.isArray(assessments)) {
      return {
        stats: { total: 0, completed: 0, pending: 0, inProgress: 0 },
        strandProgress: [],
        pendingTasks: [],
        completedTasks: [],
        recentFeedback: [],
        nextMilestone: { type: "first", target: 1, current: 0 },
      };
    }

    const completedCount = assessments.filter((a) => a.status === "signed_off");
    const pendingCount = assessments.filter((a) => a.status === "pending");
    const inProgressCount = assessments.filter((a) => ["uploading", "processing", "draft_ready", "under_review"].includes(a.status));
    
    // Calculate strand progress
    const modeCounts: Record<string, number> = { presenting: 0, explaining: 0, persuading: 0 };
    completedCount.forEach((a) => {
      if (a.mode === "presenting") modeCounts.presenting++;
      else if (a.mode === "explaining") modeCounts.explaining++;
      else if (a.mode === "persuading") modeCounts.persuading++;
    });

    // Only the physical strand is actually built/displayed with a count — the
    // others render "Coming Soon", so their counts are placeholders (0).
    const strands = [
      { name: "physical", label: "Physical", emoji: "🗣️", count: modeCounts.presenting },
      { name: "linguistic", label: "Linguistic", emoji: "📝", count: 0 },
      { name: "cognitive", label: "Cognitive", emoji: "🧠", count: 0 },
      { name: "social-emotional", label: "Social-Emotional", emoji: "💬", count: 0 },
    ];

    // Pending tasks — only truly fresh (pending) ones link to the recorder. An
    // uploading task already has a recording in flight, so it belongs in progress,
    // not on a widget that points back at the recorder.
    const pending = assessments
      .filter((a) => a.status === "pending")
      .slice(0, 3)
      .map((a) => ({
        id: a.id,
        title: a.prompt_name || `${a.mode.charAt(0).toUpperCase() + a.mode.slice(1)} Assessment`,
        mode: a.mode,
        cohort: a.cohort_name || "General",
        status: a.status,
        date: a.created_at,
      }));

    // Completed tasks (recent)
    const completedTasksList = completedCount
      .sort((a, b) => new Date(b.completed_at || 0).getTime() - new Date(a.completed_at || 0).getTime())
      .slice(0, 5)
      .map((a) => ({
        id: a.id,
        title: a.prompt_name || `${a.mode.charAt(0).toUpperCase() + a.mode.slice(1)} Assessment`,
        mode: a.mode,
        cohort: a.cohort_name || "General",
        status: a.status,
        date: a.completed_at || a.created_at,
      }));

    // Recent feedback
    const feedback = assessments
      .filter((a) => a.status === "signed_off" || a.status === "draft_ready" || a.status === "under_review")
      .sort((a, b) => new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime())
      .slice(0, 3)
      .map((a) => ({
        id: a.id,
        title: a.prompt_name || `${a.mode.charAt(0).toUpperCase() + a.mode.slice(1)} Assessment`,
        mode: a.mode,
        status: a.status,
        date: a.completed_at || a.updated_at || a.created_at,
      }));

    // Next milestone
    const milestones = [1, 5, 10, 25, 50];
    const nextMilestone = milestones.find(m => m > completedCount.length) || 50;

    return {
      stats: { total: assessments.length, completed: completedCount.length, pending: pendingCount.length, inProgress: inProgressCount.length },
      strandProgress: strands,
      pendingTasks: pending,
      completedTasks: completedTasksList,
      recentFeedback: feedback,
      nextMilestone: { type: nextMilestone === 1 ? "first" : "progress", target: nextMilestone, current: completedCount.length },
    };
  }, [assessments]);

  return (
    <AppLayout role="student">
      <div className="space-y-8">
        {/* Welcome Header */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-2xl font-bold text-foreground mb-1">
            {user ? `Welcome back, ${user.first_name} 👋` : "Loading... 👋"}
          </h1>
          <p className="text-muted-foreground">Track your oracy skills progress</p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 border-blue-200">
              <CardContent className="flex items-center gap-4 p-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Mic className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{stats.total}</p>
                  <p className="text-xs text-blue-600/70">Total Tasks</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950 dark:to-green-900 border-green-200">
              <CardContent className="flex items-center gap-4 p-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-700 dark:text-green-300">{stats.completed}</p>
                  <p className="text-xs text-green-600/70">Completed</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-950 dark:to-yellow-900 border-yellow-200">
              <CardContent className="flex items-center gap-4 p-4">
                <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-700 dark:text-yellow-300">{stats.inProgress}</p>
                  <p className="text-xs text-yellow-600/70">In Progress</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900 border-purple-200">
              <CardContent className="flex items-center gap-4 p-4">
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{stats.pending}</p>
                  <p className="text-xs text-purple-600/70">Pending</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Skills Progress */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Your Skills Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {strandProgress.map((strand, idx) => {
                  const isBuilt = strand.name === "physical";
                  const inner = (
                    <div className={`flex flex-col items-center text-center p-4 rounded-xl ${isBuilt ? "bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer" : "bg-secondary/20 opacity-60"}`}>
                      <span className="text-3xl mb-2">{strand.emoji}</span>
                      <p className="font-medium text-sm mb-1">{strand.label}</p>
                      {isBuilt ? (
                        <>
                          <p className="text-2xl font-bold text-primary">{strand.count}</p>
                          <p className="text-xs text-muted-foreground">assessments</p>
                          <div className="mt-2 w-full h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary rounded-full transition-all"
                              style={{ width: `${Math.min(100, (strand.count / 10) * 100)}%` }}
                            />
                          </div>
                          <span className="mt-3 flex items-center gap-1 text-xs font-medium text-primary">
                            View assessments
                            <ArrowRight className="w-3 h-3" />
                          </span>
                        </>
                      ) : (
                        <p className="text-sm text-muted-foreground">Coming Soon</p>
                      )}
                    </div>
                  );
                  return isBuilt ? (
                    <Link key={strand.name} to="/student/tasks">{inner}</Link>
                  ) : (
                    <div key={strand.name}>{inner}</div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pending Tasks */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
            <Card className="h-full">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="w-5 h-5 text-yellow-500" />
                  Upcoming Tasks
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <p className="text-center text-muted-foreground py-8">Loading...</p>
                ) : pendingTasks.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
                    <p className="font-medium text-foreground">All caught up!</p>
                    <p className="text-sm text-muted-foreground mb-4">No pending tasks</p>
                    <StartSelfServeButton className="flex flex-col items-center" />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingTasks.map((task) => {
                      const statusInfo = getStatusDisplay(task.status);
                      return (
                        <Link key={task.id} to={`/student/task/${task.id}`}>
                          <div className="flex items-center gap-3 p-3 rounded-lg border hover:bg-secondary/50 transition-colors">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${statusInfo.bg}`}>
                              {task.status === "pending" ? <Clock className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{task.title}</p>
                              <p className="text-xs text-muted-foreground">{task.cohort}</p>
                            </div>
                            <ArrowRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Recent Completions */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Card className="h-full">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Award className="w-5 h-5 text-green-500" />
                  Recent Completions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {completedTasks.length === 0 ? (
                  <div className="text-center py-8">
                    <Star className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                    <p className="font-medium text-foreground">No completed tasks yet</p>
                    <p className="text-sm text-muted-foreground">Complete your first assessment!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {completedTasks.map((task) => (
                      <Link key={task.id} to={`/student/feedback/${task.id}`}>
                        <div className="flex items-center gap-3 p-3 rounded-lg border hover:bg-secondary/50 transition-colors">
                          <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{task.title}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <ModeBadge mode={task.mode === "presenting" ? "presentational" : task.mode === "explaining" ? "exploratory" : "collaborative"} className="text-[10px]" />
                              <span className="text-xs text-muted-foreground">
                                {task.date ? new Date(task.date).toLocaleDateString() : ""}
                              </span>
                            </div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Milestone Banner */}
        {stats.completed > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}>
            <Card className="bg-gradient-to-r from-amber-500 to-orange-500 border-none text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                      <Trophy className="w-8 h-8" />
                    </div>
                    <div>
                      <p className="text-lg font-semibold">Great progress!</p>
                      <p className="text-white/80">
                        You've completed {stats.completed} assessment{stats.completed !== 1 ? "s" : ""}. 
                        {nextMilestone.target > stats.completed && (
                          <> Only {nextMilestone.target - stats.completed} more to reach {nextMilestone.target} assessments!</>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="hidden md:block">
                    <div className="flex items-center gap-2 text-sm">
                      <span>{stats.completed}</span>
                      <span>/</span>
                      <span>{nextMilestone.target}</span>
                    </div>
                    <div className="w-32 h-2 bg-white/30 rounded-full mt-1 overflow-hidden">
                      <div 
                        className="h-full bg-white rounded-full" 
                        style={{ width: `${(stats.completed / nextMilestone.target) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Quick Actions */}
        {/* <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <Link to="/student/tasks">
                  <Button className="gap-2">
                    <Plus className="w-4 h-4" />
                    Start New Task
                  </Button>
                </Link>
                <Link to="/student/progress">
                  <Button variant="outline" className="gap-2">
                    <BarChart3 className="w-4 h-4" />
                    View Progress
                  </Button>
                </Link>
                <Link to="/student/feedback">
                  <Button variant="outline" className="gap-2">
                    <FileText className="w-4 h-4" />
                    My Reports
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div> */}
      </div>
    </AppLayout>
  );
}
